import React, { useState, useEffect } from 'react';
import { Plus, X, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/DataTable/DataTable';
import Breadcrumb from '../../components/Common/Breadcrumb';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import HelpModal from '../../components/HelpModal/HelpModal';
import { burnExpenseService } from '../../services/burnExpenseService';
import { toast } from 'react-toastify';
import '../../components/ConfirmationModal/ConfirmationModal.css';

const GROUPS = ['Salary', 'Infra', 'Office', 'Marketing', 'Travel', 'Compliance', 'Other'];

const BurnExpenseCategoryMaster = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    group: 'Other',
    isRecurringDefault: false,
    isActive: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await burnExpenseService.getCategories();
      if (response?.success) {
        setData((response.data || []).map(item => ({ ...item, id: item._id })));
      } else {
        toast.error('Failed to load categories');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', group: 'Other', isRecurringDefault: false, isActive: true });
    setEditingItem(null);
  };

  const handleAdd = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      group: item.group || 'Other',
      isRecurringDefault: !!item.isRecurringDefault,
      isActive: item.isActive !== false
    });
    setShowModal(true);
  };

  const handleDelete = (item) => {
    setDeleteItem(item);
    setShowDeleteModal(true);
  };

  const handleSave = async () => {
    if (!formData.name?.trim()) {
      toast.error('Category name is required');
      return;
    }
    try {
      if (editingItem) {
        const res = await burnExpenseService.updateCategory(editingItem._id, formData);
        if (res?.success) {
          toast.success('Category updated');
          setShowModal(false);
          loadData();
        } else {
          toast.error(res?.message || 'Failed to update');
        }
      } else {
        const res = await burnExpenseService.createCategory(formData);
        if (res?.success) {
          toast.success('Category created');
          setShowModal(false);
          loadData();
        } else {
          toast.error(res?.message || 'Failed to create');
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save');
    }
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    try {
      const res = await burnExpenseService.deleteCategory(deleteItem._id);
      if (res?.success) {
        toast.success('Category deleted');
        setShowDeleteModal(false);
        setDeleteItem(null);
        loadData();
      } else {
        toast.error(res?.message || 'Failed to delete');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to delete');
    }
  };

  const columns = [
    { accessorKey: 'name', header: 'Name', cell: ({ getValue }) => <span className="fw-medium">{getValue()}</span> },
    { accessorKey: 'group', header: 'Group', cell: ({ getValue }) => <span className="badge bg-primary">{getValue()}</span> },
    { accessorKey: 'isRecurringDefault', header: 'Recurring Default', cell: ({ getValue }) => getValue() ? 'Yes' : 'No' },
    { accessorKey: 'isActive', header: 'Status', cell: ({ getValue }) => <span className={`badge ${getValue() ? 'bg-success' : 'bg-secondary'}`}>{getValue() ? 'Active' : 'Inactive'}</span> }
  ];

  const breadcrumbItems = [
    { label: 'Expenses', path: '/expenses', onClick: () => navigate('/expenses') },
    { label: 'Masters', path: '/expenses?tab=masters', onClick: () => navigate('/expenses?tab=masters') },
    { label: 'Burn Expense Category', path: '/expenses/burn-category-master', isCurrent: true }
  ];

  const helpContent = (
    <>
      <h3>What is Burn Expense Category Master?</h3>
      <p>This screen lets you <span className="help-term">manage expense categories used for burn tracking</span>—the types of operational spend (e.g. Rent, Server &amp; Hosting, Legal &amp; CA) that appear when recording burn expense entries. Categories are grouped (Salary, Infra, Office, etc.) for reporting and filtering.</p>

      <h3>Navigation</h3>
      <p>Use the breadcrumb links <span className="help-term">Expenses</span> or <span className="help-term">Masters</span> to go back. No separate back arrow is shown here; navigation is through the breadcrumb trail only.</p>

      <h3>Adding and editing categories</h3>
      <p>Click <span className="help-term">+ Add Category</span> to create a new category. In the form you can set:</p>
      <ul>
        <li><strong>Name *</strong> – Display name (e.g. Rent, SaaS Tools).</li>
        <li><strong>Group</strong> – Category group: Salary, Infra, Office, Marketing, Travel, Compliance, or Other.</li>
        <li><strong>Recurring default</strong> – When checked, new burn entries using this category default to &quot;Recurring&quot; (e.g. monthly rent).</li>
        <li><strong>Active</strong> – Uncheck to hide the category from use without deleting it.</li>
      </ul>
      <p>Use the <strong>Action</strong> menu (three dots) on a row to <strong>Edit</strong> or <strong>Delete</strong> a category.</p>

      <h3>Table columns</h3>
      <ul>
        <li><strong>Name</strong> – Category name.</li>
        <li><strong>Group</strong> – Group badge (e.g. Infra, Compliance).</li>
        <li><strong>Recurring Default</strong> – Yes if new entries default to recurring.</li>
        <li><strong>Status</strong> – Active (available) or Inactive (hidden).</li>
        <li><strong>Action</strong> – Edit and Delete.</li>
      </ul>

      <h3>Search</h3>
      <p>Use the search bar to filter categories by name or group. The count shows how many entries are currently displayed.</p>
    </>
  );

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          showBackButton={false}
          rightSlot={
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
              onClick={() => setShowHelp(true)}
              title="Help"
              aria-label="Open help"
            >
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>
      <div className="page-header">
        <div className="page-title">
          <h1>Burn Expense Category Master</h1>
          <p>Manage expense categories for burn tracking</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={handleAdd}><Plus size={16} /> Add Category</button>
        </div>
      </div>
      <div className="data-table-section">
        <DataTable data={data} columns={columns} loading={loading} onEdit={handleEdit} onDelete={handleDelete} noCard pageSize={10} />
      </div>

      {showModal && (
        <div className="confirmation-modal-overlay" onClick={(e) => e.target === e.currentTarget && (setShowModal(false), resetForm())}>
          <div className="confirmation-modal" style={{ maxWidth: '420px' }}>
            <div className="confirmation-modal-header">
              <h3 className="confirmation-modal-title mb-0">{editingItem ? 'Edit Category' : 'Add Category'}</h3>
              <button className="confirmation-modal-close" onClick={() => (setShowModal(false), resetForm())} aria-label="Close"><X size={20} /></button>
            </div>
            <div className="confirmation-modal-content" style={{ padding: '1.5rem' }}>
              <div className="mb-3">
                <label className="form-label">Name *</label>
                <input type="text" className="form-control" value={formData.name} onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Rent" />
              </div>
              <div className="mb-3">
                <label className="form-label">Group</label>
                <select className="form-select" value={formData.group} onChange={(e) => setFormData(f => ({ ...f, group: e.target.value }))}>
                  {GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div className="form-check mb-3">
                <input type="checkbox" className="form-check-input" id="rec" checked={formData.isRecurringDefault} onChange={(e) => setFormData(f => ({ ...f, isRecurringDefault: e.target.checked }))} />
                <label className="form-check-label" htmlFor="rec">Recurring default</label>
              </div>
              <div className="form-check">
                <input type="checkbox" className="form-check-input" id="act" checked={formData.isActive} onChange={(e) => setFormData(f => ({ ...f, isActive: e.target.checked }))} />
                <label className="form-check-label" htmlFor="act">Active</label>
              </div>
            </div>
            <div className="confirmation-modal-footer" style={{ padding: '1rem 1.5rem' }}>
              <button className="confirmation-modal-cancel" onClick={() => (setShowModal(false), resetForm())}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}>{editingItem ? 'Update' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal isOpen={showDeleteModal} onClose={() => (setShowDeleteModal(false), setDeleteItem(null))} onConfirm={confirmDelete}
        title="Delete Category" message={`Delete "${deleteItem?.name}"?`} confirmText="Delete" type="danger" />

      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title="Burn Expense Category Master – Help"
      >
        {helpContent}
      </HelpModal>
    </div>
  );
};

export default BurnExpenseCategoryMaster;
